import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CircleAlert, Megaphone, MessageSquare } from 'lucide-react';
import { useMemo, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';

import { hasPermission, PERMISSIONS, useCurrentMembership } from '@/lib/permissions';

import {
  CampaignDetailDialog,
  type CampaignDetail,
} from '@/components/messaging/campaign-detail-dialog';
import {
  ComposeMessageDialog,
  type ComposeDraft,
} from '@/components/messaging/compose-message-dialog';
import { CreateListDialog } from '@/components/messaging/create-list-dialog';
import { MessageAutomationsPanel } from '@/components/messaging/message-automations-panel';
import { MessageListsPanel } from '@/components/messaging/message-lists-panel';
import { MessageTemplatesPanel } from '@/components/messaging/message-templates-panel';
import { TemplateDialog } from '@/components/messaging/template-dialog';
import { CollapsibleInsights } from '@/components/shared/collapsible-insights';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import {
  DateRangeFilter,
  DASHBOARD_DATE_RANGE_PRESETS,
  resolvePreset,
  type DateRange,
} from '@/components/shared/date-range-filter';
import { PageHeader } from '@/components/shared/page-header';
import { StatCard } from '@/components/shared/stat-card';
import { Badge, type BadgeVariant } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { usePreferences } from '@/stores/preferences-store';
import { formatDateTime, formatNumber, formatPercent } from '@/utils/format';

interface CampaignRow {
  id: string;
  name: string;
  channels: Array<'push' | 'sms' | 'email'>;
  status: CampaignDetail['status'];
  resolvedCount: number;
  soleRecipientName: string | null;
  deliveredCount: number;
  failedCount: number;
  estimatedCostMinor: number;
  currency: string;
  subject: string | null;
  body: string;
  audienceType: ComposeDraft['audienceType'];
  audienceRef: string | null;
  audienceMemberIds: string[] | null;
  templateId: string | null;
  sentAt: string | null;
  scheduledAt: string | null;
  createdAt: string;
}

interface MessagingInsightsMetric {
  value: number;
  comparison: {
    change: number;
    previousValue: number;
  };
}

interface MessagingInsights {
  messagesDelivered: MessagingInsightsMetric;
  campaignsSent: MessagingInsightsMetric;
  deliveryFailures: MessagingInsightsMetric;
}

const formatChange = (change: number): string => {
  const formatted = formatPercent(Math.abs(change));
  if (change > 0) return `+${formatted}`;
  if (change < 0) return `-${formatted}`;
  return formatted;
};

const campaignStatusVariant = (status: CampaignDetail['status']): BadgeVariant => {
  if (status === 'sent') return 'success';
  if (status === 'failed' || status === 'partially_failed') return 'danger';
  if (status === 'cancelled') return 'neutral';
  if (status === 'scheduled' || status === 'sending') return 'info';
  return 'neutral';
};

const campaignListDate = (row: CampaignRow): string | null =>
  row.sentAt ?? row.scheduledAt ?? row.createdAt ?? null;

export const MessagingPage = () => {
  const { t } = useTranslation('messaging');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const timezone = usePreferences((state) => state.timezone);
  const locale = usePreferences((state) => state.locale);
  const queryClient = useQueryClient();
  const membership = useCurrentMembership();
  const canSend = hasPermission(membership, PERMISSIONS.MESSAGING_SEND_SMS);
  const canManageLists = hasPermission(membership, PERMISSIONS.MESSAGING_MANAGE_LISTS);
  const canManageTemplates = hasPermission(membership, PERMISSIONS.MESSAGING_MANAGE_TEMPLATES);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialTab = searchParams.get('tab') ?? 'campaigns';
  const [tab, setTab] = useState(initialTab);
  const [insightsDateRange, setInsightsDateRange] = useState<DateRange>(() => ({
    preset: 'thisMonth',
    ...resolvePreset('thisMonth', timezone),
  }));
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeDraft, setComposeDraft] = useState<ComposeDraft | null>(null);
  const [listOpen, setListOpen] = useState(false);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CampaignRow | CampaignDetail | null>(null);

  const smsCapacity = useQuery({
    queryKey: ['messaging', 'sms-capacity'],
    queryFn: () =>
      api.get<{
        mode: 'byok' | 'trinity';
        isConfigured: boolean;
        segmentLength: number;
        affordableSingleSegmentTexts: number;
      }>('/messaging/sms-capacity'),
    enabled: canSend,
  });

  useEffect(() => {
    if (searchParams.get('tab') !== 'credits') return;
    const next = new URLSearchParams(searchParams);
    next.delete('tab');
    const checkout = next.get('checkout');
    const sessionId = next.get('sessionId');
    const purchase = next.get('purchase');
    const suffix = new URLSearchParams();
    if (checkout) suffix.set('checkout', checkout);
    if (sessionId) suffix.set('sessionId', sessionId);
    if (purchase) suffix.set('purchase', purchase);
    const query = suffix.toString();
    navigate(query ? `/settings/hubtel/credits?${query}` : '/settings/hubtel/credits', {
      replace: true,
    });
  }, [navigate, searchParams]);

  const handleTabChange = useCallback(
    (value: string) => {
      setTab(value);
      const next = new URLSearchParams(searchParams);
      if (value === 'campaigns') {
        next.delete('tab');
      } else {
        next.set('tab', value);
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const list = useQuery({
    queryKey: ['messaging', 'campaigns'],
    queryFn: () => api.get<{ data: CampaignRow[] }>('/messaging/campaigns'),
  });

  const dateFrom = insightsDateRange.from ?? undefined;
  const dateTo = insightsDateRange.to ?? undefined;

  const insights = useQuery({
    queryKey: ['messaging', 'insights', dateFrom, dateTo],
    queryFn: () =>
      api.get<MessagingInsights>('/messaging/insights', {
        query: { dateFrom, dateTo },
      }),
  });

  const dateRangeLabels = useMemo(
    () => ({
      today: tCommon('date.today'),
      yesterday: tCommon('date.yesterday'),
      thisWeek: tCommon('date.thisWeek'),
      lastWeek: tCommon('date.lastWeek'),
      thisMonth: tCommon('date.thisMonth'),
      lastMonth: tCommon('date.lastMonth'),
      thisQuarter: tCommon('date.thisQuarter'),
      thisYear: tCommon('date.thisYear'),
      lastYear: tCommon('date.lastYear'),
      allTime: tCommon('date.allTime'),
      custom: tCommon('date.custom'),
      from: t('insights.dateFrom'),
      to: t('insights.dateTo'),
    }),
    [t, tCommon],
  );

  const openCompose = (draft: ComposeDraft | null = null) => {
    setComposeDraft(draft);
    setComposeOpen(true);
  };

  const composeDraftFromDetail = (detail: CampaignDetail): ComposeDraft => ({
    campaignId:
      detail.status === 'draft' || detail.status === 'scheduled' ? detail.id : undefined,
    name: detail.name,
    subject: detail.subject,
    body: detail.body,
    channels: detail.channels.filter((c): c is 'sms' => c === 'sms'),
    audienceType:
      detail.audienceType === 'filter' ? 'whole_church' : detail.audienceType,
    audienceRef: detail.audienceRef,
    audienceMemberIds: detail.audienceMemberIds,
    templateId: detail.templateId,
    scheduledAt: detail.status === 'scheduled' ? detail.scheduledAt : null,
    repeatInterval: detail.repeatInterval ?? 'none',
    repeatUntil: detail.repeatUntil ?? null,
    reuseKind: detail.status === 'draft' ? 'send' : 'resend',
  });

  const openResendFromDetail = (detail: CampaignDetail) => {
    setDetailId(null);
    openCompose(composeDraftFromDetail(detail));
  };

  const resend = async (row: CampaignRow) => {
    setResendingId(row.id);
    try {
      const detail = await api.get<CampaignDetail>(`/messaging/campaigns/${row.id}`);
      openCompose(composeDraftFromDetail(detail));
    } catch (err) {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    } finally {
      setResendingId(null);
    }
  };

  const deleteDraft = useMutation({
    mutationFn: (id: string) => api.delete(`/messaging/campaigns/${id}`),
    onSuccess: () => {
      toast.success(t('campaigns.deleted'));
      setDeleteTarget(null);
      setDetailId(null);
      void queryClient.invalidateQueries({ queryKey: ['messaging', 'campaigns'] });
      void queryClient.invalidateQueries({ queryKey: ['messaging', 'insights'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const columns: DataTableColumn<CampaignRow>[] = [
    {
      id: 'date',
      header: t('campaigns.columns.date'),
      cell: (row) => {
        const when = campaignListDate(row);
        return when ? formatDateTime(when, { locale, timezone }) : '—';
      },
    },
    {
      id: 'name',
      header: t('campaigns.columns.name'),
      cell: (row) => row.name,
      hideBelowMd: true,
    },
    {
      id: 'recipients',
      header: t('campaigns.columns.recipients'),
      cell: (row) => {
        const name = row.soleRecipientName?.trim();
        if (name) return name;
        return t('campaigns.recipients', { count: row.resolvedCount });
      },
    },
    {
      id: 'delivery',
      header: t('campaigns.columns.delivery'),
      cell: (row) =>
        t('campaigns.delivered', {
          delivered: row.deliveredCount,
          failed: row.failedCount,
        }),
      hideBelowMd: true,
    },
    {
      id: 'status',
      header: t('campaigns.columns.status'),
      cell: (row) => (
        <Badge variant={campaignStatusVariant(row.status)}>
          {t(`campaigns.statusLabel.${row.status}`)}
        </Badge>
      ),
    },
  ];

  const headerActions = (
    <div className="flex items-center gap-4">
      {tab === 'campaigns' && canSend ? (
        <>
          <span
            className="flex items-baseline gap-1.5 tabular-nums"
            title={t('campaigns.smsCapacityHint', {
              length: smsCapacity.data?.segmentLength ?? 160,
            })}
          >
            <span className="text-section-title font-semibold text-fg">
              {smsCapacity.isLoading
                ? '…'
                : formatNumber(smsCapacity.data?.affordableSingleSegmentTexts ?? 0)}
            </span>
            {!smsCapacity.isLoading ? (
              <span className="text-body-sm font-medium text-fg-secondary">
                {t('campaigns.smsCapacitySuffix', {
                  count: smsCapacity.data?.affordableSingleSegmentTexts ?? 0,
                })}
              </span>
            ) : null}
          </span>
          <Button onClick={() => openCompose(null)}>{t('campaigns.create')}</Button>
        </>
      ) : tab === 'lists' && canManageLists ? (
        <Button onClick={() => setListOpen(true)}>{t('lists.create')}</Button>
      ) : tab === 'templates' && canManageTemplates ? (
        <Button onClick={() => setTemplateOpen(true)}>{t('templates.create')}</Button>
      ) : null}
    </div>
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('title')}
        description={t('description')}
        headerActions={headerActions}
      />

      <CollapsibleInsights
        storageKey="trinity.insights.messaging.open"
        summary={[
          {
            label: t('insights.messagesDelivered'),
            value: insights.data ? formatNumber(insights.data.messagesDelivered.value) : '…',
          },
          {
            label: t('insights.campaignsSent'),
            value: insights.data ? formatNumber(insights.data.campaignsSent.value) : '…',
          },
          {
            label: t('insights.deliveryFailures'),
            value: insights.data ? formatNumber(insights.data.deliveryFailures.value) : '…',
          },
        ]}
      >
        <DateRangeFilter
          value={insightsDateRange}
          onChange={setInsightsDateRange}
          labels={dateRangeLabels}
          presets={DASHBOARD_DATE_RANGE_PRESETS}
        />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <StatCard
            label={t('insights.messagesDelivered')}
            value={
              insights.data ? formatNumber(insights.data.messagesDelivered.value) : undefined
            }
            icon={<MessageSquare className="size-4" aria-hidden />}
            loading={insights.isLoading}
            comparison={
              insights.data
                ? {
                    change: insights.data.messagesDelivered.comparison.change,
                    label: t('insights.vsPreviousPeriod'),
                    formatted: formatChange(insights.data.messagesDelivered.comparison.change),
                  }
                : undefined
            }
            tipId="dashboard.messages.delivered"
          />
          <StatCard
            label={t('insights.campaignsSent')}
            value={insights.data ? formatNumber(insights.data.campaignsSent.value) : undefined}
            icon={<Megaphone className="size-4" aria-hidden />}
            loading={insights.isLoading}
            comparison={
              insights.data
                ? {
                    change: insights.data.campaignsSent.comparison.change,
                    label: t('insights.vsPreviousPeriod'),
                    formatted: formatChange(insights.data.campaignsSent.comparison.change),
                  }
                : undefined
            }
            tipId="dashboard.messages.campaigns"
          />
          <StatCard
            label={t('insights.deliveryFailures')}
            value={
              insights.data ? formatNumber(insights.data.deliveryFailures.value) : undefined
            }
            icon={<CircleAlert className="size-4" aria-hidden />}
            loading={insights.isLoading}
            comparison={
              insights.data
                ? {
                    change: insights.data.deliveryFailures.comparison.change,
                    label: t('insights.vsPreviousPeriod'),
                    formatted: formatChange(insights.data.deliveryFailures.comparison.change),
                  }
                : undefined
            }
            tipId="dashboard.messages.failures"
          />
        </div>
      </CollapsibleInsights>

      <Tabs value={tab} onValueChange={handleTabChange}>
        <TabsList>
          <TabsTrigger value="campaigns">{t('tabs.campaigns')}</TabsTrigger>
          <TabsTrigger value="lists">{t('tabs.lists')}</TabsTrigger>
          <TabsTrigger value="templates">{t('tabs.templates')}</TabsTrigger>
          <TabsTrigger value="automations">{t('tabs.automations')}</TabsTrigger>
        </TabsList>

        <TabsContent value="campaigns">
          <DataTable
            rows={list.data?.data ?? []}
            columns={columns}
            rowKey={(row) => row.id}
            loading={list.isLoading}
            empty={<p className="text-body-sm text-fg-secondary">{t('campaigns.empty')}</p>}
            onRowClick={(row) => setDetailId(row.id)}
            actionsHeader={tCommon('table.actions')}
            rowActions={(row) => (
              <div className="flex justify-end gap-2">
                {row.status === 'draft' ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={(event) => {
                      event.stopPropagation();
                      setDeleteTarget(row);
                    }}
                  >
                    {t('campaigns.delete')}
                  </Button>
                ) : null}
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={resendingId === row.id}
                  onClick={(event) => {
                    event.stopPropagation();
                    void resend(row);
                  }}
                >
                  {row.status === 'draft' ? t('campaigns.send') : t('campaigns.resend')}
                </Button>
              </div>
            )}
          />
        </TabsContent>

        <TabsContent value="lists">
          <MessageListsPanel />
        </TabsContent>

        <TabsContent value="templates">
          <MessageTemplatesPanel />
        </TabsContent>

        <TabsContent value="automations">
          <MessageAutomationsPanel />
        </TabsContent>
      </Tabs>

      <CampaignDetailDialog
        campaignId={detailId}
        open={detailId !== null}
        onOpenChange={(next) => {
          if (!next) setDetailId(null);
        }}
        onResend={openResendFromDetail}
        onDeleteDraft={(campaign) => setDeleteTarget(campaign)}
        resending={resendingId !== null}
      />
      <ComposeMessageDialog
        open={composeOpen}
        onOpenChange={(next) => {
          setComposeOpen(next);
          if (!next) setComposeDraft(null);
        }}
        draft={composeDraft}
      />
      <CreateListDialog open={listOpen} onOpenChange={setListOpen} />
      <TemplateDialog open={templateOpen} onOpenChange={setTemplateOpen} />
      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(next) => {
          if (!next) setDeleteTarget(null);
        }}
        title={t('campaigns.confirmDelete.title', { name: deleteTarget?.name ?? '' })}
        description={t('campaigns.confirmDelete.description')}
        confirmLabel={t('campaigns.confirmDelete.confirm')}
        cancelLabel={tCommon('action.cancel')}
        destructive
        loading={deleteDraft.isPending}
        onConfirm={() => {
          if (deleteTarget) deleteDraft.mutate(deleteTarget.id);
        }}
      />
    </div>
  );
};
